import { FaCheck, FaChevronLeft, FaStar, FaUserGraduate } from "react-icons/fa";
import { HiOutlineExclamationTriangle } from "react-icons/hi2";
import Button from "../../../components/Button";

export const Step3Tutors = ({
  formData,
  loading,
  enforceAvailability,
  availableTutors,
  updateForm,
  toggleTutor,
  prevStep,
  canSubmit
}) => {
  const renderSkeletonLoader = () => (
    <div className="animate-pulse space-y-4">
      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
      <div className="h-10 bg-gray-200 rounded"></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[1, 2].map((i) => (
          <div key={i} className="h-24 bg-gray-200 rounded-lg"></div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="border-l-4 border-l-primary pl-4">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <FaUserGraduate className="w-5 h-5 text-primary" />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-text">
            Choose Your Tutors
          </h2>
        </div>
      </div>

      {enforceAvailability && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex gap-3">
          <FaCheck className="text-green-600 w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-green-900">
            <p className="font-medium">
              Availability enforcement is active
            </p>
            <p className="text-green-800 mt-1">
              Only tutors available for your selected time slots are
              shown.
            </p>
          </div>
        </div>
      )}

      <label className="flex items-start gap-3 p-4 border-2 border-border rounded-lg cursor-pointer hover:border-primary/50">
        <input
          type="checkbox"
          checked={formData.useRandom}
          onChange={(e) =>
            updateForm({
              useRandom: e.target.checked,
              selectedTutorIds: [],
            })
          }
          className="w-5 h-5 text-primary mt-0.5"
        />
        <div>
          <p className="font-medium text-text">
            Automatic Tutor Selection
          </p>
          <p className="text-sm text-text/60 mt-1">
            Let the system randomly select 3 qualified tutors
            {enforceAvailability && " from available tutors"}
          </p>
        </div>
      </label>

      {!formData.useRandom && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-text">
              Select tutors manually
            </p>
            <span className="text-sm text-text/60">
              {formData.selectedTutorIds.length}/3 selected
            </span>
          </div>

          {loading.tutors ? (
            renderSkeletonLoader()
          ) : availableTutors.length === 0 ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-50 mb-4">
                <HiOutlineExclamationTriangle className="w-8 h-8 text-amber-500" />
              </div>
              <h3 className="text-lg font-semibold text-text mb-2">
                No Available Tutors
              </h3>
              <p className="text-text/60 text-sm max-w-md mx-auto">
                No tutors are available for your selected time slots.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {availableTutors.map((tutor) => {
                const isSelected = formData.selectedTutorIds.includes(
                  tutor._id
                );
                return (
                  <div
                    key={tutor._id}
                    onClick={() => toggleTutor(tutor._id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleTutor(tutor._id);
                      }
                    }}
                    className={`relative p-4 border-2 rounded-lg cursor-pointer ${
                      isSelected
                        ? "border-primary bg-primary/5 shadow-md"
                        : "border-border hover:border-primary/50 hover:shadow-sm"
                    }`}
                  >
                    {isSelected && (
                      <div className="absolute top-3 right-3 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                        <FaCheck className="w-3 h-3 text-white" />
                      </div>
                    )}
                    <div className="pr-8">
                      <h3 className="font-semibold text-text text-lg mb-1">
                        {tutor.name?.first} {tutor.name?.last}
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-text/70 mb-3">
                        <FaStar className="text-yellow-500 w-4 h-4" />
                        <span>
                          {tutor.profile?.rating?.toFixed(1) || "N/A"}
                        </span>
                      </div>
                      {enforceAvailability && (
                        <div className="flex items-center gap-2 text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                          <FaCheck className="w-3 h-3" />
                          Available for your times
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="flex gap-3 pt-4 border-t border-border">
        <Button
          type="button"
          onClick={prevStep}
          variant="outline"
          className="w-full sm:w-auto"
        >
          <FaChevronLeft className="mr-2 w-4 h-4" />
          Back
        </Button>
        <Button
          type="submit"
          disabled={!canSubmit}
          loading={loading.form}
          className="flex-1 sm:flex-initial"
        >
          {loading.form ? "Submitting..." : "Submit Booking Request"}
        </Button>
      </div>
    </div>
  );
};
